import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowLeft, CheckCircle, Square, Save, Wifi, WifiOff, BookOpen } from 'lucide-react';

const OWNER_ROLES = new Set(['CEO', 'SYSTEM OWNER / ADMINISTRATOR', 'SYSTEM OWNER', 'SL1']);
const isOwnerRole = (role) => OWNER_ROLES.has(String(role || '').trim().toUpperCase());

const COLORS = {
  navyBlue: '#003366', orange: '#FF6B35', mustard: '#FFAA00', green: '#16A34A',
  lightGray: '#F9FAFB', white: '#FFFFFF', borderGray: '#D1D5DB', textGray: '#6B7280',
  textDark: '#1F2937', red: '#DC2626',
};

const fetchWithTimeout = (url, options = {}, timeoutMs = 30000) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  const token = window.sessionStorage.getItem('nwaAccessToken');
  const headers = { ...(options.headers || {}) };
  if (token) headers.Authorization = `Bearer ${token}`;
  return fetch(url, { ...options, headers, signal: controller.signal })
    .finally(() => clearTimeout(timer));
};

const safeJson = (value, fallback = []) => {
  if (Array.isArray(value) || (value && typeof value === 'object')) return value;
  if (!value) return fallback;
  try { return JSON.parse(value); } catch (_) { return fallback; }
};

const lessonNo = (row) => Number(row?.['Lesson Number'] || row?.lesson_number || 0);
const lessonId = (row) => row?.['Lesson ID'] || row?.lesson_id || '';
const lessonStatus = (row) => row?.Status || row?.status || '';
const iepId = (row) => row?.['IEP ID'] || row?.iep_id || '';

const lessonStatusFromMastery = (score) => ({ '0':'Not achieved', '1':'Continue', '2':'Partially achieved', '3':'Achieved' }[String(score)] || '');

const emptyOutcome = (iepTargetId) => ({
  iep_target_id: iepTargetId,
  lesson_status: '', mastery_score: '',
  'Evidence / Brief Comment': '', 'Worked Well': '',
  'Did Not Work / Needs Changing': '', 'Next Lesson Implication': '',
});

export default function LearningPlansScreen({ student, apiUrl, userRole, onBack }) {
  const [bundle, setBundle] = useState(null);
  const [selectedIepId, setSelectedIepId] = useState('');
  const [selectedLessonId, setSelectedLessonId] = useState('');
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [syncState, setSyncState] = useState(navigator.onLine ? 'synced' : 'offline');
  const [draftDirty, setDraftDirty] = useState(false);
  const [expandedLtlpDomain, setExpandedLtlpDomain] = useState('');
  const [expandedIepTargetId, setExpandedIepTargetId] = useState('');
  const [actionBusy, setActionBusy] = useState('');
  const draftRevisionRef = useRef(0);
  const syncInFlightRef = useRef(null);
  const completingRef = useRef(false);
  const isOwner = isOwnerRole(userRole);
  const ownerReviewDraft = bundle?.learning_plan_release_status === 'Draft - Owner Review';
  const currentCycleNumber = Number(bundle?.learning_plan_current_cycle_number || 1);

  const loadBundle = async () => {
    setLoading(true); setError(''); setNotice('');
    const offlineKey = `nwaActiveLessonCache:${student.student_code}`;
    try {
      const r = await fetchWithTimeout(`${apiUrl}/api/learning-plans/${student.student_code}`);
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not load learning plans');
      setBundle(d);
      const cycles = d.iep_cycles || [];
      const active = cycles.find((x) => String(x.Status || '').toLowerCase() === 'active') || cycles[cycles.length - 1];
      if (active) {
        setSelectedIepId(active['IEP ID']);
        const activeLessons = (d.lessons || []).filter((x) => x['IEP ID'] === active['IEP ID']).sort((a, b) => lessonNo(a) - lessonNo(b));
        const current = activeLessons.find((x) => !['Locked', 'Completed'].includes(lessonStatus(x))) || activeLessons[activeLessons.length - 1];
        if (current && d.learning_plan_release_status === 'Active') {
          const currentId = lessonId(current);
          const minimal = {
            learning_plans_ready: d.learning_plans_ready, learning_plans_missing: [],
            ltlp_needs: [], iep_cycles: [active],
            iep_targets: (d.iep_targets || []).filter((x) => x['IEP ID'] === active['IEP ID']),
            lessons: [current],
            lesson_targets: (d.lesson_targets || []).filter((x) => x['Lesson ID'] === currentId),
            offline_cached_at: new Date().toISOString(),
          };
          window.localStorage.setItem(offlineKey, JSON.stringify(minimal));
        } else {
          window.localStorage.removeItem(offlineKey);
        }
      } else {
        window.localStorage.removeItem(offlineKey);
      }
    } catch (e) {
      const cached = window.localStorage.getItem(offlineKey);
      if (cached) {
        try {
          const d = JSON.parse(cached);
          setBundle(d);
          const active = (d.iep_cycles || [])[0];
          if (active) setSelectedIepId(active['IEP ID']);
          setNotice('Offline mode: showing the last synced active lesson saved on this device. Draft notes will stay on this device until the connection returns.');
          setSyncState('offline');
          return;
        } catch (_) {}
      }
      setError(e.message || 'Could not load learning plans');
    } finally { setLoading(false); }
  };

  useEffect(() => { loadBundle(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [student.student_code]);

  const selectedIep = useMemo(
    () => (bundle?.iep_cycles || []).find((x) => x['IEP ID'] === selectedIepId) || null,
    [bundle, selectedIepId]
  );
  const lessons = useMemo(
    () => (bundle?.lessons || []).filter((x) => x['IEP ID'] === selectedIepId).sort((a, b) => lessonNo(a) - lessonNo(b)),
    [bundle, selectedIepId]
  );
  const selectedLesson = useMemo(
    () => lessons.find((x) => lessonId(x) === selectedLessonId) || null,
    [lessons, selectedLessonId]
  );
  const selectedLessonTargets = useMemo(
    () => (bundle?.lesson_targets || []).filter((x) => x['Lesson ID'] === selectedLessonId),
    [bundle, selectedLessonId]
  );
  const selectedIepTargets = useMemo(
    () => (bundle?.iep_targets || []).filter((x) => x['IEP ID'] === selectedIepId),
    [bundle, selectedIepId]
  );
  const ltlpGroups = useMemo(() => {
    const groups = {};
    (bundle?.ltlp_needs || []).forEach((need) => {
      const domain = need.Domain || 'Other';
      if (!groups[domain]) groups[domain] = [];
      groups[domain].push(need);
    });
    return groups;
  }, [bundle]);

  useEffect(() => {
    if (!selectedIepId || lessons.length === 0) return;
    const current = lessons.find((x) => !['Locked', 'Completed'].includes(lessonStatus(x))) || lessons[lessons.length - 1];
    if (!selectedLessonId || !lessons.some((x) => lessonId(x) === selectedLessonId)) setSelectedLessonId(lessonId(current));
  }, [selectedIepId, lessons, selectedLessonId]);

  useEffect(() => {
    if (!selectedLesson) { setDraft(null); setDraftDirty(false); return; }
    const outcomes = selectedLessonTargets.map((t) => ({
      iep_target_id: t['IEP Target ID'],
      lesson_status: t['Lesson Status'] || '',
      mastery_score: t['Mastery Score'] === '' || t['Mastery Score'] == null ? '' : String(t['Mastery Score']),
      'Evidence / Brief Comment': t['Evidence / Brief Comment'] || '',
      'Worked Well': t['Worked Well'] || '',
      'Did Not Work / Needs Changing': t['Did Not Work / Needs Changing'] || '',
      'Next Lesson Implication': t['Next Lesson Implication'] || '',
    }));
    const base = {
      target_outcomes: outcomes.length ? outcomes : selectedLessonTargets.map((t) => emptyOutcome(t['IEP Target ID'])),
      overall_reflection: selectedLesson['Overall Reflection'] || '',
      consolidation_implications: selectedLesson['Consolidation / Implications for Next Lesson'] || '',
    };
    const key = ownerReviewDraft
      ? `nwaOwnerReviewPreview:${student.student_code}:${bundle?.learning_plan_draft_revision || 1}:${lessonId(selectedLesson)}`
      : `nwaLessonDraft:${student.student_code}:${lessonId(selectedLesson)}`;
    const local = window.localStorage.getItem(key);
    if (local && !['Locked', 'Completed'].includes(lessonStatus(selectedLesson))) {
      try { setDraft({ ...base, ...JSON.parse(local) }); setDraftDirty(true); draftRevisionRef.current += 1; setSyncState(navigator.onLine ? 'local' : 'offline'); return; } catch (_) {}
    }
    setDraft(base);
    setDraftDirty(false);
    draftRevisionRef.current = 0;
    setSyncState(navigator.onLine ? 'synced' : 'offline');
  }, [selectedLesson, selectedLessonTargets, student.student_code, ownerReviewDraft, bundle?.learning_plan_draft_revision]);

  useEffect(() => {
    if (!draftDirty || !draft || !selectedLesson || ['Locked', 'Completed'].includes(lessonStatus(selectedLesson))) return;
    const key = ownerReviewDraft
      ? `nwaOwnerReviewPreview:${student.student_code}:${bundle?.learning_plan_draft_revision || 1}:${lessonId(selectedLesson)}`
      : `nwaLessonDraft:${student.student_code}:${lessonId(selectedLesson)}`;
    window.localStorage.setItem(key, JSON.stringify(draft));
    if (!navigator.onLine) setSyncState('offline');
  }, [draft, draftDirty, selectedLesson, student.student_code, ownerReviewDraft, bundle?.learning_plan_draft_revision]);

  const markDraftChanged = () => {
    draftRevisionRef.current += 1;
    setDraftDirty(true);
    setSyncState(navigator.onLine ? 'local' : 'offline');
  };

  const updateOutcome = (targetId, field, value) => {
    setDraft((prev) => ({
      ...prev,
      target_outcomes: (prev?.target_outcomes || []).map((o) => {
        if (o.iep_target_id !== targetId) return o;
        if (field === 'mastery_score') return { ...o, mastery_score: value, lesson_status: lessonStatusFromMastery(value) };
        return { ...o, [field]: value };
      }),
    }));
    markDraftChanged();
  };

  const updateLessonField = (field, value) => {
    setDraft((prev) => ({ ...prev, [field]: value }));
    markDraftChanged();
  };

  const syncDraft = async ({ quiet = true } = {}) => {
    if (ownerReviewDraft) return true;
    if (completingRef.current) return true;
    if (!draft || !selectedLesson) return true;
    if (!navigator.onLine) { setSyncState('offline'); return false; }
    if (syncInFlightRef.current) return syncInFlightRef.current;

    const payload = JSON.parse(JSON.stringify(draft));
    const revision = draftRevisionRef.current;
    const currentLessonId = lessonId(selectedLesson);
    const run = (async () => {
      setSaving(true); setSyncState('saving'); if (!quiet) setError('');
      try {
        const r = await fetchWithTimeout(`${apiUrl}/api/learning-plans/${student.student_code}/lessons/${currentLessonId}/draft`, {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(d.error || 'Could not save lesson');
        setBundle((prev) => prev ? {
          ...prev,
          lessons: (prev.lessons || []).map((x) => lessonId(x) === currentLessonId ? {
            ...x, Status: d.lesson?.status || x.Status || 'In progress',
            'Overall Reflection': payload.overall_reflection || '',
            'Consolidation / Implications for Next Lesson': payload.consolidation_implications || '',
          } : x),
          lesson_targets: (prev.lesson_targets || []).map((x) => {
            const out = (payload.target_outcomes || []).find((o) => o.iep_target_id === x['IEP Target ID']);
            return out && x['Lesson ID'] === currentLessonId ? {
              ...x, 'Lesson Status': out.lesson_status || '', 'Mastery Score': out.mastery_score ?? '',
              'Evidence / Brief Comment': out['Evidence / Brief Comment'] || '', 'Worked Well': out['Worked Well'] || '',
              'Did Not Work / Needs Changing': out['Did Not Work / Needs Changing'] || '',
              'Next Lesson Implication': out['Next Lesson Implication'] || '',
            } : x;
          }),
        } : prev);
        if (revision === draftRevisionRef.current) {
          window.localStorage.removeItem(`nwaLessonDraft:${student.student_code}:${currentLessonId}`);
          setDraftDirty(false);
          setSyncState('synced');
        } else {
          setSyncState('local');
        }
        return true;
      } catch (e) {
        setSyncState(navigator.onLine ? 'local' : 'offline');
        if (!quiet) setError(`${e.message || 'Could not sync'} — your work is still saved on this device.`);
        return false;
      } finally {
        setSaving(false);
      }
    })();

    syncInFlightRef.current = run;
    try {
      return await run;
    } finally {
      if (syncInFlightRef.current === run) syncInFlightRef.current = null;
    }
  };

  // Auto-save to the server after a short pause. Local device storage is written immediately above.
  useEffect(() => {
    if (ownerReviewDraft || !draftDirty || !draft || !selectedLesson || !navigator.onLine || ['Locked', 'Completed'].includes(lessonStatus(selectedLesson))) return;
    const timer = window.setTimeout(() => { syncDraft({ quiet: true }); }, 3000);
    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, draftDirty, selectedLessonId]);

  useEffect(() => {
    const offline = () => setSyncState('offline');
    const online = async () => {
      if (!draft || !selectedLesson || ['Locked', 'Completed'].includes(lessonStatus(selectedLesson))) {
        setSyncState('synced');
        return;
      }
      if (!draftDirty) { setSyncState('synced'); return; }
      setSyncState('local');
      const ok = await syncDraft({ quiet: true });
      setNotice(ok ? 'Connection restored — your lesson notes have synced.' : 'Connection returned, but your notes have not synced yet. They are still saved on this device.');
    };
    window.addEventListener('online', online); window.addEventListener('offline', offline);
    return () => { window.removeEventListener('online', online); window.removeEventListener('offline', offline); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draft, draftDirty, selectedLessonId]);

  const completeLesson = async () => {
    if (ownerReviewDraft) { setError('Activate the Learning Plans before lesson delivery begins.'); return; }
    if (!navigator.onLine) { setError('Reconnect to the internet before completing this lesson. Your draft remains saved on this device.'); return; }
    if (!draft || !selectedLesson) return;

    const id = lessonId(selectedLesson);
    const payload = JSON.parse(JSON.stringify(draft));
    completingRef.current = true;
    setCompleting(true);
    setError('');
    setNotice('Completing lesson…');
    setSyncState('saving');
    try {
      // If an autosave was already in flight, allow it to settle first. The
      // completion request still carries the current draft and is authoritative.
      if (syncInFlightRef.current) await syncInFlightRef.current;
      const r = await fetchWithTimeout(`${apiUrl}/api/learning-plans/${student.student_code}/lessons/${id}/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not complete lesson');
      setBundle(d.bundle);
      window.localStorage.removeItem(`nwaLessonDraft:${student.student_code}:${id}`);
      setDraftDirty(false);
      const next = d.next_lesson;
      if (next?.lesson_id) setSelectedLessonId(next.lesson_id);
      setSyncState('synced');
      setNotice(next?.lesson_id ? `Lesson completed — Lesson ${next.lesson_number || ''} generated.` : 'Lesson completed and saved.');
    } catch (e) {
      setSyncState(navigator.onLine ? 'local' : 'offline');
      setError(`${e.message || 'Could not complete lesson'} — your work is still saved on this device.`);
      setNotice('');
    } finally {
      completingRef.current = false;
      setCompleting(false);
    }
  };

  const runOwnerAction = async (action) => {
    if (!isOwner) return;
    setActionBusy(action); setError(''); setNotice('');
    const endpoint = action === 'generate' ? 'draft' : action === 'regenerate' ? 'regenerate-draft' : action === 'archive' ? 'archive-superseded' : 'activate';
    try {
      if (action === 'archive' && !window.confirm('Archive the superseded draft revisions? The current draft and all screening evidence will be kept.')) return;
      const r = await fetchWithTimeout(`${apiUrl}/api/learning-plans/${student.student_code}/${endpoint}`, { method: 'POST' });
      const d = await r.json();
      if (!r.ok) throw new Error(d.error || 'Could not update Learning Plans');
      if (d.bundle) {
        setBundle(d.bundle);
        const cycles = d.bundle.iep_cycles || [];
        const currentIep = cycles[cycles.length - 1];
        if (currentIep) {
          setSelectedIepId(currentIep['IEP ID']);
          const currentLessons = (d.bundle.lessons || [])
            .filter((x) => x['IEP ID'] === currentIep['IEP ID'])
            .sort((a, b) => lessonNo(a) - lessonNo(b));
          const currentLesson = currentLessons.find((x) => !['Locked', 'Completed'].includes(lessonStatus(x))) || currentLessons[currentLessons.length - 1];
          if (currentLesson) setSelectedLessonId(lessonId(currentLesson));
        }
      } else {
        await loadBundle();
      }
      const noRemainingTargets = d?.reason === 'no_remaining_ltlp_targets' || d?.bundle?.learning_plan_complete;
      setNotice(noRemainingTargets
        ? `No IEP ${currentCycleNumber + 1} was created — no unresolved LTLP targets remain.`
        : action === 'activate'
          ? `IEP ${currentCycleNumber} activated — the assigned educator can now use it.`
          : action === 'archive'
            ? `${d.archived_revision_count || 0} superseded draft revision(s) archived safely.`
            : action === 'regenerate'
              ? 'A new private draft revision has been generated for your review.'
              : 'Private Learning Plans draft generated for your review.');
    } catch (e) {
      setError(e.message || 'Could not update Learning Plans');
    } finally { setActionBusy(''); }
  };

  const isLocked = selectedLesson && ['Locked', 'Completed'].includes(lessonStatus(selectedLesson));
  const statusBadge = completing
    ? <span style={{ color: COLORS.mustard, display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}><Save size={14}/>Completing lesson…</span>
    : syncState === 'synced'
    ? <span style={{ color: COLORS.green, display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}><CheckCircle size={14}/>Saved ✓</span>
    : syncState === 'saving'
      ? <span style={{ color: COLORS.mustard, display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}><Save size={14}/>Saving…</span>
      : syncState === 'offline'
        ? <span style={{ color: COLORS.orange, display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}><WifiOff size={14}/>Saved on device — waiting to sync</span>
        : <span style={{ color: COLORS.orange, display: 'flex', gap: 5, alignItems: 'center', fontSize: 12 }}><Wifi size={14}/>Saved on device — syncing automatically</span>;

  const card = { background: COLORS.white, borderRadius: 9, boxShadow: '0 2px 7px rgba(0,0,0,.07)', padding: 14, marginBottom: 10 };
  const input = { width: '100%', boxSizing: 'border-box', border: `1px solid ${COLORS.borderGray}`, borderRadius: 6, padding: '7px 9px', fontSize: 13, lineHeight:1.35, background: isLocked ? '#F3F4F6' : '#fff', textAlign:'left' };
  const label = { fontSize: 12, fontWeight: 700, color: COLORS.textDark, marginBottom: 4, display: 'block', textAlign:'left' };

  if (loading) return <div style={{ minHeight:'100vh', background:COLORS.lightGray, padding:24 }}>Loading learning plans…</div>;

  return <div style={{ minHeight:'100vh', background:COLORS.lightGray }}>
    <div style={{ background:COLORS.navyBlue, color:'#fff', padding:'10px 16px', borderBottom:`2px solid ${COLORS.mustard}` }}>
      <div style={{ maxWidth:1180, margin:'0 auto', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <button onClick={onBack} style={{ background:'none', border:'none', color:'#fff', display:'flex', gap:7, alignItems:'center', fontSize:14, padding:'8px 0' }}><ArrowLeft size={18}/>Back</button>
        <div style={{ textAlign:'right' }}><div style={{ fontWeight:700 }}>{student.full_name}</div><div style={{ fontSize:12, opacity:.8 }}>Learning Plans</div></div>
      </div>
    </div>

    <div style={{ maxWidth:1180, margin:'0 auto', padding:'12px 12px 40px' }}>
      {error && <div style={{ ...card, borderLeft:`5px solid ${COLORS.red}`, color:'#991B1B' }}>{error}</div>}
      {notice && <div style={{ ...card, borderLeft:`5px solid ${COLORS.mustard}`, color:COLORS.textDark, fontSize:13 }}>{notice}</div>}
      {!bundle?.learning_plans_ready && <div style={{ ...card, borderLeft:`5px solid ${COLORS.mustard}` }}>
        <h2 style={{ margin:'0 0 8px', color:COLORS.navyBlue, fontSize:18 }}>Learning Plans not unlocked yet</h2>
        <p style={{ margin:'0 0 8px', color:COLORS.textGray }}>The Learning Plans control appears only after the required baseline screening set is complete.</p>
        {(bundle?.learning_plans_missing || []).length > 0 && <p style={{ margin:0, fontSize:13 }}><strong>Still required:</strong> {(bundle.learning_plans_missing || []).join(', ')}</p>}
      </div>}

      {bundle?.learning_plans_ready && (bundle?.iep_cycles || []).length === 0 && <div style={{ ...card, borderLeft:`5px solid ${COLORS.green}` }}>
        <div style={{ display:'flex', gap:10, alignItems:'center', justifyContent:'space-between', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}><CheckCircle size={22} color={COLORS.green}/><div><strong>Screening complete — Learning Plans ready.</strong><div style={{ color:COLORS.textGray, fontSize:13, marginTop:3 }}>{isOwner ? 'Generate a private draft to review before the assigned educator can see it.' : 'The System Owner will review and activate the Learning Plans before lesson delivery begins.'}</div></div></div>
          {isOwner && <button disabled={!!actionBusy} onClick={()=>runOwnerAction('generate')} style={{ border:'none', borderRadius:6, background:COLORS.navyBlue, color:'#fff', padding:'8px 12px', fontWeight:700, cursor:'pointer', opacity:actionBusy?.6:1 }}>{actionBusy==='generate'?'Generating…':'Generate Draft'}</button>}
        </div>
      </div>}

      {bundle?.learning_plans_ready && bundle?.learning_plan_complete && (bundle?.iep_cycles || []).length > 0 && !ownerReviewDraft && <div style={{ ...card, borderLeft:`5px solid ${COLORS.green}` }}>
        <div style={{ display:'flex', gap:10, alignItems:'center' }}>
          <CheckCircle size={22} color={COLORS.green}/>
          <div>
            <strong>Learning plan complete — no unresolved LTLP targets remain.</strong>
            <div style={{ color:COLORS.textGray, fontSize:13, marginTop:3 }}>Completed IEP and lesson history remains available for reference. A new IEP is not generated unless new authorised assessment/planning evidence creates a genuine learning need.</div>
          </div>
        </div>
      </div>}

      {bundle?.learning_plans_ready && isOwner && bundle?.learning_plans_can_generate_draft && (bundle?.iep_cycles || []).length > 0 && !ownerReviewDraft && <div style={{ ...card, borderLeft:`5px solid ${COLORS.green}` }}>
        <div style={{ display:'flex', gap:10, alignItems:'center', justifyContent:'space-between', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <CheckCircle size={22} color={COLORS.green}/>
            <div>
              <strong>IEP {currentCycleNumber} complete — IEP {currentCycleNumber + 1} is ready to draft.</strong>
              <div style={{ color:COLORS.textGray, fontSize:13, marginTop:3 }}>Generate the next private draft, review its targets, then activate it when you are satisfied.</div>
            </div>
          </div>
          <button disabled={!!actionBusy} onClick={()=>runOwnerAction('generate')} style={{ border:'none', borderRadius:6, background:COLORS.navyBlue, color:'#fff', padding:'8px 12px', fontWeight:700, cursor:'pointer', opacity:actionBusy?.6:1 }}>
            {actionBusy==='generate' ? 'Generating…' : `Generate IEP ${currentCycleNumber + 1} Draft`}
          </button>
        </div>
      </div>}

      {ownerReviewDraft && isOwner && <div style={{ ...card, borderLeft:`5px solid ${COLORS.mustard}`, background:'#FFFBEB' }}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:12, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ textAlign:'left' }}><strong>IEP {currentCycleNumber} Draft – Content Validation</strong><div style={{ color:COLORS.textGray, fontSize:12, marginTop:3, textAlign:'left' }}>Revision {bundle?.learning_plan_draft_revision || 1}. This content is private for validation; the assigned educator cannot see or use it yet.</div></div>
          <div style={{ display:'flex', gap:7, flexWrap:'wrap' }}>
            {(bundle?.learning_plan_superseded_revision_count || 0) > 0 && <button disabled={!!actionBusy} onClick={()=>runOwnerAction('archive')} style={{ border:'1px solid #D1D5DB', borderRadius:6, background:'#fff', color:COLORS.textGray, padding:'7px 10px', fontWeight:700, cursor:'pointer', opacity:actionBusy?.6:1 }}>{actionBusy==='archive'?'Archiving…':`Archive ${bundle.learning_plan_superseded_revision_count} old draft${bundle.learning_plan_superseded_revision_count===1?'':'s'}`}</button>}
            <button disabled={!!actionBusy} onClick={()=>runOwnerAction('regenerate')} style={{ border:`1px solid ${COLORS.navyBlue}`, borderRadius:6, background:'#fff', color:COLORS.navyBlue, padding:'7px 10px', fontWeight:700, cursor:'pointer', opacity:actionBusy?.6:1 }}>{actionBusy==='regenerate'?'Regenerating…':'Regenerate Draft'}</button>
            <button disabled={!!actionBusy} onClick={()=>runOwnerAction('activate')} style={{ border:'none', borderRadius:6, background:COLORS.green, color:'#fff', padding:'7px 10px', fontWeight:700, cursor:'pointer', opacity:actionBusy?.6:1 }}>{actionBusy==='activate'?'Activating…':`Activate Learning Plans — IEP ${currentCycleNumber}`}</button>
          </div>
        </div>
      </div>}

      {(bundle?.ltlp_needs || []).length > 0 && <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'center', marginBottom:8 }}>
          <h2 style={{ margin:0, color:COLORS.navyBlue, fontSize:15 }}>LTLP</h2>
          <span style={{ color:COLORS.textGray, fontSize:11 }}>Reference only — tap a target area for details</span>
        </div>
        <div style={{ display:'flex', gap:10, flexWrap:'wrap', alignItems:'flex-start' }}>
          {Object.entries(ltlpGroups).map(([domain, needs]) => {
            const open = expandedLtlpDomain === domain;
            const activeCount = needs.filter((n) => String(n.Status || '').trim().toLowerCase() === 'active').length;
            return <button key={domain} onClick={() => setExpandedLtlpDomain(open ? '' : domain)} style={{ width:70, border:'none', background:'transparent', padding:0, cursor:'pointer', color:COLORS.textDark }}>
              <span style={{ width:30, height:30, borderRadius:6, margin:'0 auto 4px', display:'flex', alignItems:'center', justifyContent:'center', border:open?`2px solid ${COLORS.navyBlue}`:'1px solid #BFC7D1', background:open?'#EFF6FF':'#fff', color:COLORS.green, fontWeight:900, fontSize:17 }}>✓</span>
              <span style={{ display:'block', fontSize:11, fontWeight:700, lineHeight:1.15 }}>{domain}</span>
              <span style={{ display:'block', fontSize:9, color:COLORS.textGray, marginTop:2 }}>{activeCount} active</span>
            </button>;
          })}
        </div>
        {expandedLtlpDomain && ltlpGroups[expandedLtlpDomain] && <div style={{ marginTop:10, borderTop:'1px solid #E5E7EB', paddingTop:8 }}>
          {ltlpGroups[expandedLtlpDomain].map((n) => <div key={n['LTLP Need ID']} style={{ padding:'5px 0', fontSize:12, textAlign:'left' }}>
            <div style={{ display:'flex', justifyContent:'flex-start', gap:8, textAlign:'left' }}><strong>{n['Need / Challenge']}</strong></div>
            <div style={{ color:COLORS.textGray, marginTop:3, textAlign:'left' }}><strong style={{ color:COLORS.textDark }}>Expected outcome:</strong> {n['Expected Outcome'] || '—'}</div>
            <div style={{ color:COLORS.textGray, marginTop:2, textAlign:'left' }}><strong style={{ color:COLORS.textDark }}>Evidence:</strong> {n['Evidence Source'] || '—'}</div>
            <div style={{ color:COLORS.textGray, marginTop:2, textAlign:'left' }}><strong style={{ color:COLORS.textDark }}>Status:</strong> {n.Status || '—'}</div>
          </div>)}
        </div>}
      </div>}

      {selectedIep && safeJson(selectedIep['Practitioner Considerations JSON']).length > 0 && <div style={{ ...card, textAlign:'left', borderLeft:`4px solid ${COLORS.orange}` }}>
        <strong style={{ display:'block', color:COLORS.navyBlue, fontSize:13, marginBottom:6 }}>Practitioner considerations</strong>
        {safeJson(selectedIep['Practitioner Considerations JSON']).map((item, idx) => <div key={idx} style={{ display:'flex', alignItems:'flex-start', gap:6, fontSize:11, color:COLORS.textDark, lineHeight:1.45, marginTop:idx?6:0, textAlign:'left' }}><span aria-hidden="true" style={{ flex:'0 0 8px', lineHeight:1.45 }}>•</span><span style={{ flex:1, minWidth:0 }}>{item}</span></div>)}
      </div>}

      {(bundle?.iep_cycles || []).length > 0 && <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'center', flexWrap:'wrap' }}>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', alignItems:'center', textAlign:'left' }}>
            <strong style={{ color:COLORS.navyBlue, fontSize:13 }}>IEPs</strong>
            {(bundle.iep_cycles || []).map((i) => {
              const displayStatus = String(i['Release Status'] || '') === 'Draft - Owner Review' ? 'Inactive' : (i.Status || '');
              return <button key={i['IEP ID']} onClick={() => { setSelectedIepId(i['IEP ID']); setExpandedIepTargetId(''); }} style={{ border:selectedIepId===i['IEP ID']?`1.5px solid ${COLORS.navyBlue}`:'1px solid #D1D5DB', background:selectedIepId===i['IEP ID']?'#EFF6FF':'#fff', borderRadius:14, padding:'4px 8px', fontWeight:700, fontSize:10, color:COLORS.navyBlue }}>IEP {i['Cycle Number']} — {displayStatus}</button>;
            })}
          </div>
          <span style={{ color:COLORS.textGray, fontSize:10 }}>Tap a target for reference</span>
        </div>
        {selectedIep && <>
          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:8 }}>
            {selectedIepTargets.map((t, idx) => {
              const tid=t['IEP Target ID']; const open=expandedIepTargetId===tid;
              return <button key={tid} onClick={() => setExpandedIepTargetId(open?'':tid)} style={{ border:open?`1.5px solid ${COLORS.orange}`:'1px solid #D1D5DB', background:open?'#FFF7ED':'#fff', borderRadius:6, padding:'5px 7px', fontSize:10, fontWeight:700, color:COLORS.textDark }}>T{idx+1} — {t.Domain || 'Target'}</button>;
            })}
          </div>
          {expandedIepTargetId && (() => { const t=selectedIepTargets.find((x)=>x['IEP Target ID']===expandedIepTargetId); return t ? <div style={{ marginTop:7, padding:'7px 9px', background:'#F9FAFB', borderRadius:6, fontSize:11 }}><strong>{t.Target}</strong>{t['Success Criteria'] && <div style={{ color:COLORS.textGray, marginTop:2 }}>Success: {t['Success Criteria']}</div>}</div> : null; })()}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(6, minmax(58px, 1fr))', gap:5, marginTop:9 }}>
            {[1,2,3,4,5,6].map((n) => {
              const l = lessons.find((x) => lessonNo(x)===n); const status = l ? lessonStatus(l) : 'Not generated';
              return <button key={n} disabled={!l} onClick={() => l && setSelectedLessonId(lessonId(l))} style={{ minHeight:38, border:l && selectedLessonId===lessonId(l)?`1.5px solid ${COLORS.orange}`:'1px solid #D1D5DB', borderRadius:6, background:l?'#fff':'#F3F4F6', color:l?COLORS.textDark:'#9CA3AF', padding:'4px 3px', fontWeight:700, fontSize:10 }}>L{n}<div style={{ fontWeight:500, marginTop:2, fontSize:8, lineHeight:1.05 }}>{status}</div></button>;
            })}
          </div>
        </>}
      </div>}

      {selectedLesson && draft && <div style={card}>
        <div style={{ display:'flex', justifyContent:'space-between', gap:10, alignItems:'center', marginBottom:8 }}>
          <div style={{ display:'flex', gap:8, alignItems:'baseline', textAlign:'left' }}><h2 style={{ margin:0, color:COLORS.navyBlue, fontSize:17 }}>Lesson {lessonNo(selectedLesson)}</h2><span style={{ fontSize:10, color:COLORS.textGray }}>{lessonStatus(selectedLesson)}</span></div>
          {ownerReviewDraft ? <span style={{ color:'#92400E', fontSize:11, fontWeight:700 }}>Review only — not released</span> : statusBadge}
        </div>

        {selectedLessonTargets.map((t, idx) => {
          const tid=t['IEP Target ID']; const outcome=(draft.target_outcomes||[]).find((o)=>o.iep_target_id===tid) || emptyOutcome(tid);
          const masteryOptions=[['0','Not demonstrated'],['1','Emerging'],['2','Developing'],['3','Secure']];
          return <div key={t['Lesson Target ID'] || tid} style={{ borderTop:idx? '1px solid #E5E7EB':'none', paddingTop:idx?10:0, marginTop:idx?10:0 }}>
            <div style={{ display:'flex', gap:7, alignItems:'baseline', flexWrap:'wrap', textAlign:'left' }}><span style={{ fontSize:10, fontWeight:800, color:COLORS.orange, textTransform:'uppercase' }}>{t.Domain || 'Target'} — T{idx+1}</span><span style={{ fontSize:9, color:COLORS.textGray, fontWeight:700 }}>{t['Duration Minutes'] || 10} min</span><strong style={{ fontSize:13 }}>{t.Target}</strong></div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(220px,1fr))', gap:10, marginTop:6, textAlign:'left' }}>
              <div style={{ fontSize:11, textAlign:'left' }}>
                <strong style={{ display:'block', marginBottom:2, textAlign:'left' }}>Teaching:</strong>
                <div style={{ textAlign:'left', lineHeight:1.45 }}>{safeJson(t['Teaching Strategy']).map((item, lineIdx)=><div key={lineIdx} style={{ marginBottom:lineIdx===safeJson(t['Teaching Strategy']).length-1?0:3 }}>{item}</div>)}</div>
              </div>
              <div style={{ fontSize:11, textAlign:'left' }}>
                <strong style={{ display:'block', marginBottom:2, textAlign:'left' }}>Reinforcement Strategy:</strong>
                <div style={{ textAlign:'left', lineHeight:1.45 }}>{safeJson(t['Reinforcement Strategy']).map((item, lineIdx)=><div key={lineIdx} style={{ marginBottom:lineIdx===safeJson(t['Reinforcement Strategy']).length-1?0:3 }}>{item}</div>)}</div>
                {safeJson(t.Resources).length>0 && <div style={{ marginTop:5, fontSize:10, color:COLORS.textGray, textAlign:'left' }}><strong style={{ color:COLORS.textDark }}>Resources:</strong> {safeJson(t.Resources).join('; ')}</div>}
                {safeJson(t['Adaptation Notes']).length>0 && <div style={{ marginTop:5, fontSize:10, lineHeight:1.35, color:'#7F1D1D', textAlign:'left' }}><strong>Previous lesson:</strong> {safeJson(t['Adaptation Notes']).join(' ')}</div>}
              </div>
            </div>

            <div style={{ background:'#F9FAFB', borderRadius:7, padding:'8px 9px', marginTop:7, textAlign:'left' }}>
              <div style={{ textAlign:'left' }}>
                <label style={label}>Mastery</label>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(4, minmax(58px, 1fr))', gap:5, maxWidth:360 }}>{masteryOptions.map(([score,name])=><div key={score} style={{ textAlign:'center' }}><button type="button" disabled={isLocked || completing} onClick={()=>updateOutcome(tid,'mastery_score',score)} title={name} style={{ width:'100%', minHeight:30, border:outcome.mastery_score===score?`1.5px solid ${COLORS.green}`:'1px solid #D1D5DB', background:outcome.mastery_score===score?'#ECFDF5':'#fff', borderRadius:5, padding:'5px 7px', fontSize:10, fontWeight:700 }}>{outcome.mastery_score===score?'✓ ':''}{score}</button><div style={{ color:COLORS.textGray, fontSize:8, lineHeight:1.15, marginTop:3 }}>{name}</div></div>)}</div>
              </div>
              <div style={{ marginTop:7, textAlign:'left' }}><label style={label}>Brief evidence / comment <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(outcome['Evidence / Brief Comment']||'').length}/250)</span></label><textarea disabled={isLocked || completing} maxLength={250} rows={1} style={input} value={outcome['Evidence / Brief Comment']} onChange={(e)=>updateOutcome(tid,'Evidence / Brief Comment',e.target.value)}/></div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:8, marginTop:7, textAlign:'left' }}>
                <div style={{ textAlign:'left' }}><label style={label}>What worked? <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(outcome['Worked Well']||'').length}/250)</span></label><textarea disabled={isLocked || completing} maxLength={250} rows={1} style={input} value={outcome['Worked Well']} onChange={(e)=>updateOutcome(tid,'Worked Well',e.target.value)}/></div>
                <div style={{ textAlign:'left' }}><label style={label}>Did not work / needs changing? <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(outcome['Did Not Work / Needs Changing']||'').length}/250)</span></label><textarea disabled={isLocked || completing} maxLength={250} rows={1} style={input} value={outcome['Did Not Work / Needs Changing']} onChange={(e)=>updateOutcome(tid,'Did Not Work / Needs Changing',e.target.value)}/></div>
              </div>
              <div style={{ marginTop:7, textAlign:'left' }}><label style={label}>Implication for next lesson <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(outcome['Next Lesson Implication']||'').length}/250)</span></label><textarea disabled={isLocked || completing} maxLength={250} rows={1} style={input} value={outcome['Next Lesson Implication']} onChange={(e)=>updateOutcome(tid,'Next Lesson Implication',e.target.value)}/></div>
            </div>
          </div>;
        })}

        <div style={{ borderTop:'1px solid #E5E7EB', marginTop:10, paddingTop:9, display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(240px,1fr))', gap:8, textAlign:'left' }}>
          <div><label style={label}>Self-evaluation / reflection <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(draft.overall_reflection||'').length}/500)</span></label><textarea disabled={isLocked || completing} maxLength={500} rows={2} style={input} value={draft.overall_reflection||''} onChange={(e)=>updateLessonField('overall_reflection',e.target.value)}/></div>
          <div><label style={label}>Consolidation / implications for next lesson <span style={{ color:COLORS.textGray, fontWeight:400 }}>({(draft.consolidation_implications||'').length}/500)</span></label><textarea disabled={isLocked || completing} maxLength={500} rows={2} style={input} value={draft.consolidation_implications||''} onChange={(e)=>updateLessonField('consolidation_implications',e.target.value)}/></div>
        </div>

        {!isLocked && !ownerReviewDraft && <div style={{ display:'flex', justifyContent:'flex-end', gap:8, marginTop:10, alignItems:'center', flexWrap:'wrap' }}>
          <span style={{ color:COLORS.textGray, fontSize:10 }}>Draft changes auto-save. When all target evaluations are ready, select Complete lesson to finalise this lesson and generate the next one.</span>
          <button aria-label="Complete lesson" title="Finalise this lesson and generate the next lesson" disabled={saving || completing || !navigator.onLine} onClick={completeLesson} style={{ minHeight:38, border:`1.5px solid ${COLORS.green}`, borderRadius:6, background:'#fff', color:COLORS.green, padding:'8px 13px', fontWeight:700, display:'flex', gap:6, alignItems:'center', opacity:(!navigator.onLine||saving||completing)?.6:1, fontSize:12 }}><Square size={16}/>{completing ? 'Completing…' : 'Complete lesson'}</button>
        </div>}
        {ownerReviewDraft && !isLocked && <div style={{ marginTop:10, textAlign:'left' }}>
          <div style={{ color:'#92400E', fontWeight:700, display:'flex', gap:6, alignItems:'center', fontSize:12 }}><CheckCircle size={16}/>Content validation preview — evaluation fields are interactive</div>
          <div style={{ marginTop:4, fontSize:10, color:COLORS.textGray }}>Preview entries are kept only in this browser and are not written to live lesson history until the plan is activated. System Development</div>
        </div>}
        {isLocked && <div style={{ marginTop:10, textAlign:'left' }}>
          <div style={{ color:COLORS.green, fontWeight:700, display:'flex', gap:6, alignItems:'center', fontSize:12 }}><CheckCircle size={16}/>Completed lesson — retained as read-only history</div>
          <div style={{ marginTop:4, fontSize:10, color:COLORS.textGray }}>Tutor: {selectedLesson['Teacher User ID'] || 'Recorded automatically'}{selectedLesson['Completed At'] ? ` — Completed ${selectedLesson['Completed At']}` : ''}</div>
        </div>}
      </div>}

      {!selectedLesson && bundle?.learning_plans_ready && (bundle?.iep_cycles || []).length > 0 && <div style={{ ...card, textAlign:'center', color:COLORS.textGray }}><BookOpen size={30} color={COLORS.navyBlue}/><div style={{ marginTop:8 }}>No lesson has been generated yet.</div></div>}
    </div>
  </div>;
}
